/**
 * The review's state, and where a thread's card goes (session 55, `.docs/55-mockup-comments.md`).
 *
 * Both are pure, so both are tested without a frame: the reducer is every way a review moves —
 * a spot asked for, a pin pressed, Escape — and `cardPlace` is the arithmetic that puts a card
 * beside a pin without leaving the page's area.
 */

import type { MockupPin } from '@fareast/shared'

import type { PinMode } from '../../pins/protocol.js'

/** A new thread being written: about a spot, or (`pin: null`) about the whole version. */
export type Draft = {
  readonly pin: MockupPin | null
  /**
   * Which composer this is. A spot asked for again while one is being written **moves** it —
   * the words stay — so the key changes only when a new one starts.
   */
  readonly key: number
}

export type ReviewState = {
  readonly mode: PinMode
  readonly draft: Draft | null
  /** The thread whose card is open. */
  readonly openId: string | null
  /** How many drafts were started — the next one's key. */
  readonly started: number
}

export type ReviewAction =
  /** Somebody asked to comment on a spot of the page. */
  | { readonly type: 'place'; readonly pin: MockupPin }
  /** …or on the whole version. */
  | { readonly type: 'compose' }
  | { readonly type: 'open'; readonly id: string }
  /** The card closed — its button, or the comment it was for posted. */
  | { readonly type: 'close' }
  /** Escape: one step back at a time. */
  | { readonly type: 'escape' }
  | { readonly type: 'mode'; readonly mode: PinMode }

export function initialReview(openId: string | null): ReviewState {
  return { mode: 'browse', draft: null, openId, started: 0 }
}

export function reviewReducer(state: ReviewState, action: ReviewAction): ReviewState {
  switch (action.type) {
    case 'place':
      // A spot placed is the end of comment mode: the next click is the page's again.
      return state.draft === null
        ? { ...state, mode: 'browse', openId: null, ...started(state, action.pin) }
        : { ...state, mode: 'browse', openId: null, draft: { ...state.draft, pin: action.pin } }
    case 'compose':
      return { ...state, mode: 'browse', openId: null, ...started(state, null) }
    case 'open':
      return { ...state, mode: 'browse', openId: action.id, draft: null }
    case 'close':
      return { ...state, openId: null, draft: null }
    case 'escape':
      if (state.draft !== null || state.openId !== null) {
        return { ...state, openId: null, draft: null }
      }
      return { ...state, mode: 'browse' }
    case 'mode':
      // Turning comment mode on puts an open thread away; a comment half written stays, and
      // the next spot moves it.
      return {
        ...state,
        mode: action.mode,
        openId: action.mode === 'comment' ? null : state.openId,
      }
  }
}

/** A new draft, and the count it was started at. */
function started(state: ReviewState, pin: MockupPin | null) {
  return { draft: { pin, key: state.started + 1 }, started: state.started + 1 }
}

/* --------------------------------------------------------------- the card's place */

/** A box in the window, as `getBoundingClientRect` gives it. */
export type WindowBox = {
  readonly left: number
  readonly top: number
  readonly right: number
  readonly bottom: number
}

/** How far a card keeps from the edges of the page's area, and from its pin. */
export const CARD_GAP = 12

/** A pin's marker is this wide and tall; its sharp corner is the spot. */
export const PIN_SIZE = 26

/**
 * Where a card's top-left corner goes, in the window.
 *
 * Beside its pin — to the right of the marker when there is room, else to its left — with its
 * top level with the marker's, and never outside the page's area. With no pin to be beside (a
 * thread about the whole version, or one whose spot is not on the page at this width) it sits in
 * the area's top-right corner, where nothing on a page usually is.
 */
export function cardPlace(input: {
  /** The marker's tip, in the window — or null. */
  readonly tip: { readonly x: number; readonly y: number } | null
  /** The area the page is shown in. */
  readonly area: WindowBox
  readonly card: { readonly width: number; readonly height: number }
}): { readonly left: number; readonly top: number } {
  const { tip, area, card } = input
  const lowestLeft = area.left + CARD_GAP
  const highestLeft = Math.max(lowestLeft, area.right - card.width - CARD_GAP)
  const lowestTop = area.top + CARD_GAP
  const highestTop = Math.max(lowestTop, area.bottom - card.height - CARD_GAP)
  const within = (value: number, low: number, high: number) => Math.min(Math.max(value, low), high)

  if (tip === null) return { left: highestLeft, top: lowestTop }
  const right = tip.x + PIN_SIZE + CARD_GAP
  const left = right + card.width <= area.right - CARD_GAP ? right : tip.x - CARD_GAP - card.width
  return {
    left: within(left, lowestLeft, highestLeft),
    top: within(tip.y - PIN_SIZE, lowestTop, highestTop),
  }
}

/**
 * The review's address for one version, or one thread on it — how the portal pages link into
 * it (`?v=`, `?c=`, `LinkViewerPage`'s `ReviewSearchSchema`).
 */
export function reviewLinkOf(
  reviewUrl: string,
  at: { readonly v: number; readonly c?: string | undefined },
): string {
  const search = new URLSearchParams({ v: String(at.v) })
  if (at.c !== undefined) search.set('c', at.c)
  return `${reviewUrl}?${search.toString()}`
}
